import { useCallback, useMemo, useState } from "react";

import { TCollabThread, TiptapCollabProvider } from "@tiptap-pro/provider";

import { Box, IconButton } from "@mui/material";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";

import MoreVert from "@mui/icons-material/MoreVert";

import { useThreadsState } from "containers/Tiptap/components/tiptap-templates/ThreadContext";

import { MenuItem, StyledPopover } from "containers/TaskDetails/styles";

import { CommentReplies, CommentItem, CommentCardContainer } from "./styles";
import ThreadCard from "./ThreadCard";
import CommentCard from "./CommentCard";
import ThreadComposer from "./ThreadComposer";

interface ThreadsListItemProps {
  thread: TCollabThread;
  provider: TiptapCollabProvider;
  active: boolean;
  open: boolean;
}

const ThreadsListItem: React.FC<ThreadsListItemProps> = ({ thread, provider, active, open }) => {
  const {
    onClickThread,
    deleteThread,
    onHoverThread,
    onLeaveThread,
    resolveThread,
    unresolveThread,
  } = useThreadsState();

  const comments = useMemo(() => provider.getThreadComments(thread.id, true), [provider, thread]);

  const notDeletedComments = (comments && comments.filter((comment) => !comment.deletedAt)) || [];

  const firstComment = notDeletedComments[0] || null;

  const handleDeleteClick = useCallback(() => deleteThread(thread.id), [thread.id, deleteThread]);

  const handleResolveClick = useCallback(
    () => resolveThread(thread.id),
    [thread.id, resolveThread],
  );

  const handleUnresolveClick = useCallback(
    () => unresolveThread(thread.id),
    [thread.id, unresolveThread],
  );

  const editComment = useCallback(
    (commentId: string, val: string) =>
      provider.updateComment(thread.id, commentId, { content: val }),
    [provider, thread.id],
  );

  const deleteComment = useCallback(
    (commentId: string) => provider.deleteComment(thread.id, commentId, { deleteContent: true }),
    [provider, thread.id, deleteThread, firstComment],
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>): void =>
    setAnchorEl(event.currentTarget);

  return (
    <CommentItem onMouseEnter={() => onHoverThread(thread.id)} onMouseLeave={() => onLeaveThread()}>
      <ThreadCard
        id={thread.id}
        active={active}
        onClick={!open ? onClickThread : () => null}
        onClickOutside={!open ? onLeaveThread : () => null}
      >
        {open ? (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <IconButton onClick={openMenu}>
                <MoreVert />
              </IconButton>
              {thread.resolvedAt ? (
                <IconButton onClick={handleUnresolveClick}>
                  <CheckCircleIcon color="primary" />
                </IconButton>
              ) : (
                <IconButton onClick={handleResolveClick}>
                  <CheckCircleOutlineOutlinedIcon />
                </IconButton>
              )}
            </Box>

            <StyledPopover
              anchorEl={anchorEl}
              open={!!anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
            </StyledPopover>

            {notDeletedComments.map(({ id, data, content, createdAt }) => (
              <CommentCardContainer key={id}>
                <CommentCard
                  user={data.user}
                  content={content}
                  createdAt={createdAt}
                  onEdit={(val) => val && editComment(id, val)}
                  onDelete={() => deleteComment(id)}
                />
              </CommentCardContainer>
            ))}

            <ThreadComposer threadId={thread.id} provider={provider} />
          </>
        ) : null}

        {!open && firstComment && firstComment.data ? (
          <>
            <CommentCard
              key={firstComment.id}
              user={firstComment.data.user}
              content={firstComment.content}
              createdAt={firstComment.createdAt}
              onEdit={(val) => {
                if (val) {
                  editComment(firstComment.id, val);
                }
              }}
              onDelete={() => deleteComment(firstComment.id)}
            />

            {notDeletedComments.length > 1 && (
              <CommentReplies>
                {Math.max(0, notDeletedComments.length - 1) || 0}{" "}
                {(notDeletedComments.length - 1 || 0) === 1 ? "reply" : "replies"}
              </CommentReplies>
            )}
          </>
        ) : null}
      </ThreadCard>
    </CommentItem>
  );
};

export default ThreadsListItem;
